<template>
  <ValidationObserver ref="conseilObserver">
    <BookmarkCard :tag-type="tagType" :icon-name="iconName" icon-type="is-success">
      <template #content>
        <div>
          <div v-if="conseilAdded">
            <DatePickerWithValidation
              v-model="internalConseil.dateConseil"
              :validation-activated="validationActivated"
              :show-validation-errors="showValidationErrors"
              vid="conseil_date"
              :disabled="readonly"
              :label="$t('projetPage.mon-annee.conseils.date')"
              class="pl-2 pr-2"
              rules="required"
              :hide-br="true"
            ></DatePickerWithValidation>

            <SelectWithValidation
              v-model="internalConseil.type"
              :validation-activated="validationActivated"
              :show-validation-errors="showValidationErrors"
              vid="conseil_type"
              :disabled="readonly"
              :label="$t('projetPage.mon-annee.conseils.type')"
              :data="typeConseil"
              :select-labels="typeConseilLabels"
              class="pl-2 pr-2 full"
              rules="required"
            />

            <b-field class="pl-2 pr-2 full">
              <template #label>
                <div class="fieldLabel">
                  <div class="innerLabel">
                    {{ $t("projetPage.mon-annee.conseils.compte-rendu") }}
                  </div>
                </div>
              </template>
              <div class="is-flex is-horizontal-center">
                <div v-if="conseil && conseil.fichier">
                  <div class="columns">
                    <div class="column">
                      <a
                        target="_blank"
                        :href="getConseilUrl(conseil)"
                        rel="noopener"
                        class="small_file_name"
                      >
                        {{ truncatedFileName(fichier.nom) }}
                      </a>
                    </div>
                  </div>
                  <div class="columns">
                    <div class="column">
                      <b-button
                        v-if="canDelete"
                        type="is-danger is-outlined"
                        rounded
                        icon-left="trash"
                        @click="deleteConseil()"
                      >
                        {{ $t("delete") }}
                      </b-button>
                    </div>
                  </div>
                </div>
                <b-button
                  v-else
                  type="is-primary is-outlined"
                  rounded
                  :disabled="readonly || !internalConseil.dateConseil || !internalConseil.type"
                >
                  <b-upload
                    accept=".pdf"
                    required
                    :disabled="readonly || !internalConseil.dateConseil || !internalConseil.type"
                    @update:model-value="addConseil"
                  >
                    {{ $t("projetPage.mon-annee.conseils.televerser") }}
                  </b-upload>
                </b-button>
              </div>
            </b-field>
          </div>
          <div v-else class="nouveau-conseil-content">
            <br />{{ $t("projetPage.mon-annee.conseils.description") }}<br /><br />
            <b-button type="is-primary is-outlined" rounded @click="conseilAdded = true">
              {{ $t("create") }}
            </b-button>
          </div>
        </div>
      </template>
    </BookmarkCard>
  </ValidationObserver>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import BookmarkCard from "../generic_components/BookmarkCard.vue"
import SelectWithValidation from "@/views/inputs/SelectWithValidation.vue"
import DatePickerWithValidation from "@/views/inputs/DatePickerWithValidation.vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { TAG_TYPES_FOR_BOOKMARK_CARD } from "@/model/bean/EnumHelper"
import { ConseilDTO, FichierDTO, ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import Constants from "@/services/Constants"
import ConversionUtils from "@/services/ConversionUtils"
import { FileService } from "@/services/rest/utils/FileService"
import { BButton, BField, BUpload } from "buefy"

export default defineComponent({
  name: "ConseilCard",
  components: {
    BookmarkCard,
    DatePickerWithValidation,
    SelectWithValidation,
    ValidationObserver,
    BButton,
    BField,
    BUpload,
  },
  props: {
    conseil: {
      type: Object as PropType<ConseilDTO>,
      required: false,
      default: undefined,
    },
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    canDelete: {
      type: Boolean,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["add", "delete"],
  data() {
    return {
      conseilAdded: false,
      infoReportingService: InfoReportingService.INSTANCE,
      fileService: FileService.INSTANCE,
      tagType: TAG_TYPES_FOR_BOOKMARK_CARD.NOUVEAU_CONSEIL,
      iconName: "plus",
      internalConseil: new ConseilDTO(),
      fichier: new FichierDTO(),
      typeConseil: ["NORMAL", "ELARGI"],
      typeConseilLabels: [] as string[],
    }
  },
  computed: {},
  watch: {
    projetAnnuel(): void {
      requestAnimationFrame(() => {
        ;(this.$refs.conseilObserver as InstanceType<typeof ValidationObserver>).reset()
      })
    },
    conseil(): void {
      this.conseilChanged()
    },
  },
  created(): void {
    this.typeConseilLabels = this.typeConseil.map((value) => {
      return this.$t(`projetPage.mon-annee.conseils.${value}`).toString()
    })
  },
  mounted(): void {
    this.conseilChanged()
    if (this.conseil) {
      this.tagType = TAG_TYPES_FOR_BOOKMARK_CARD.CONSEIL_DES_ELEVES
      this.iconName = "users"
    } else {
      this.tagType = TAG_TYPES_FOR_BOOKMARK_CARD.NOUVEAU_CONSEIL
      this.iconName = "plus"
    }
  },
  methods: {
    addConseil(files: File | File[] | null): void {
      // b-upload peut émettre File, File[] ou null selon la configuration
      const file = Array.isArray(files) ? files[0] : files

      if (this.projetAnnuel.id && file) {
        this.conseilAdded = false
        this.$emit("add", this.internalConseil, file)
        this.conseilChanged()
        requestAnimationFrame(() => {
          ;(this.$refs.conseilObserver as InstanceType<typeof ValidationObserver>).reset()
        })
      }
    },

    deleteConseil(): void {
      this.infoReportingService.dialog(
        this.$t("confirmation").toString(),
        this.$t("projetPage.mon-annee.conseils.are-you-sure").toString(),
        this.$t("projetPage.mon-annee.conseils.supprimer").toString(),
        () => {
          this.$emit("delete", this.conseil)
        },
        "is-info",
      )
    },

    conseilChanged(): void {
      if (this.conseil) {
        this.conseilAdded = true
        this.internalConseil = ConversionUtils.convertConseilDatesToLocal(this.conseil)
        this.loadFichier()
      } else {
        this.internalConseil = new ConseilDTO()
      }
    },

    async loadFichier(): Promise<void> {
      if (this.projetAnnuel.id && this.conseil?.fichier) {
        this.fichier = await this.fileService.getFichier(this.projetAnnuel.id, this.conseil.fichier)
      }
    },

    truncatedFileName(fileName: string | undefined): string {
      if (!fileName) {
        return ""
      }
      return fileName.length > 20 ? fileName.substring(0, 20) + "..." : fileName
    },

    getConseilUrl(conseil: ConseilDTO): string {
      return Constants.apiUrlv2(
        `/projets/${this.projetAnnuel.id}/fichiers/${conseil.fichier}/content`,
      )
    },
  },
})
</script>

<style lang="scss" scoped>
.full {
  width: 100%;

  & span {
    width: 100%;
  }

  & select {
    width: 100%;
  }
}

.nouveau-conseil-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}

.stay-in-box {
  overflow-y: hidden;
}

.small_file_name {
  text-overflow: ellipsis;
  max-width: 220px;
  overflow: hidden;
  display: block;
}
</style>
